// @story HIR-HR21
/* The readiness grid on a role's pipeline (design "Interview briefs &
 * guardrails", 30 Oct 2026, §A "Keeping 25 setups manageable"): every stage
 * with what it needs set up — its written test or its AI interview brief —
 * and where that has got to: Not set, Draft, Waiting for approval, Ready.
 * Stages with nothing to set up say so. "Draft all missing" asks the
 * Assessment Designer for a brief for each AI interview stage that has none;
 * each draft still needs its own approval.
 *
 *   GET  /api/interview-briefs/role/:jobId/readiness
 *   POST /api/interview-briefs/role/:jobId/draft-missing
 *
 * `refreshKey` reloads it (the pipeline saved, a stage's panel closed);
 * `onOpen(stageId)` opens that stage's panel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, CardHeader, StatusPill } from '../../ds';

const TONE = { not_set: 'danger', draft: 'neutral', pending: 'warning', ready: 'success', none: 'neutral' };
const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' };
const cell = { padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'left', verticalAlign: 'middle' };

export default function StageReadiness({ jobId, refreshKey = 0, onOpen }) {
  const [grid, setGrid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/interview-briefs/role/${jobId}/readiness`);
      setGrid(r.data?.data || null);
    } catch (e) { setGrid(null); }
  }, [jobId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const draftMissing = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/interview-briefs/role/${jobId}/draft-missing`);
      const d = r.data?.data || {};
      setResult(d);
      if (d.grid) setGrid(d.grid);
      if ((d.drafted || []).length) toast.success(`Drafted ${d.drafted.length === 1 ? 'one brief' : `${d.drafted.length} briefs`}. Read each one, change what you need, then send it for approval.`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'The Assessment Designer could not draft them just now.');
    } finally { setBusy(false); }
  };

  if (!grid || !grid.stages?.length) return null;
  const needs = grid.stages.filter((s) => s.setup);
  if (!needs.length) return null;

  return (
    <Card>
      <CardHeader
        title="Is every stage ready?"
        action={grid.missingBriefs > 0 ? (
          <Button size="sm" variant="secondary" onClick={draftMissing} loading={busy} disabled={busy}>
            {busy ? 'Drafting — about a minute each…' : `Draft all missing (${grid.missingBriefs})`}
          </Button>
        ) : null}
      />
      <p style={{ ...small, margin: '0 0 12px' }}>
        {grid.notReady === 0
          ? 'Every test and interview brief is approved. Candidates can be invited to each stage.'
          : `${grid.notReady === 1 ? 'One stage is' : `${grid.notReady} stages are`} not ready. Set up each one and have it approved before inviting candidates to it.`}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table aria-label="Stage readiness" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 420, ...small }}>
          <thead>
            <tr>{['Stage', 'Needs', 'State', ''].map((h) => <th key={h} style={{ ...cell, fontWeight: 600, color: 'var(--text-1)' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {grid.stages.map((s) => (
              <tr key={s.stageId} style={s.enabled ? undefined : { opacity: 0.6 }}>
                <td style={cell}>{s.label}{s.enabled ? '' : ' (off)'}</td>
                <td style={cell}>{s.what || '—'}</td>
                <td style={cell}>
                  <StatusPill tone={TONE[s.state] || 'neutral'}>{s.stateText}</StatusPill>
                  {s.changeWaiting && <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>a change is waiting for approval</span>}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {s.setup && onOpen && <Button size="sm" variant="ghost" onClick={() => onOpen(s.stageId)}>Open</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result && (
        <div role="status" style={{ ...small, marginTop: 12, display: 'grid', gap: 4 }}>
          {(result.drafted || []).length > 0 && <span>Drafted: {result.drafted.join(', ')}. Each now needs reading, then its own approval.</span>}
          {(result.drafted || []).length === 0 && !(result.failed || []).length && <span>Every AI interview stage already has a brief.</span>}
          {(result.failed || []).map((f) => <span key={f.label} style={{ color: 'var(--status-danger-text)' }}>Not drafted: {f.label}. {f.why}</span>)}
        </div>
      )}
    </Card>
  );
}
